import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { wishlistService } from './wishlist.service';
import {
    CREATE_WISHLIST_ITEM,
    DELETE_WISHLIST_ITEM,
    EDIT_WISHLIST_ITEM,
    GET_WISHLIST_ITEM_BY_ID,
    GET_WISHLIST_ITEM_BY_ID_SUCCESS,
    GET_WISHLIST_ITEMS,
    GET_WISHLIST_ITEMS_SUCCESS,
    GET_WISHLIST_UPDATES_COUNT,
    GET_WISHLIST_UPDATES_COUNT_SUCCESS,
    MARK_WISHLIST_SEEN,
} from './wishlist.types';
import { httpRequestsOnErrorsActions } from '../http_requests_on_errors';
import { httpRequestsOnLoadActions } from '../http_requests_on_load';
import { httpRequestsOnSuccessActions } from '../http_requests_on_success';

const errorOf = (err) => (err?.data?.message ? err?.data?.message : err?.response?.data?.message);

function* getWishlistItems({ type, payload }) {
    if (payload?.load !== 'noLoad') {
        yield put(httpRequestsOnLoadActions.appendLoading(type));
    }
    yield put(httpRequestsOnErrorsActions.removeError(type));
    try {
        const res = yield call(wishlistService.getWishlistItemsService, payload?.params);
        yield put({
            type: GET_WISHLIST_ITEMS_SUCCESS,
            payload: { data: res.data?.data || [], count: res.data?.count || 0 },
        });
    } catch (err) {
        yield put(httpRequestsOnErrorsActions.appendError(type, errorOf(err)));
    } finally {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
    }
}

function* getWishlistItemById({ type, payload }) {
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    yield put(httpRequestsOnErrorsActions.removeError(type));
    try {
        const res = yield call(wishlistService.getWishlistItemByIdService, payload.id);
        yield put({ type: GET_WISHLIST_ITEM_BY_ID_SUCCESS, payload: res.data });
    } catch (err) {
        yield put(httpRequestsOnErrorsActions.appendError(type, errorOf(err)));
    } finally {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
    }
}

/**
 * Mutations re-fetch the current page rather than patching it locally: the
 * server assigns the request number and owns the sort order, so re-reading is
 * the only way the list stays honest about both.
 */
function* createWishlistItem({ type, payload }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(type));
    try {
        yield call(wishlistService.createWishlistItemService, payload.body);
        yield put({ type: GET_WISHLIST_ITEMS, payload: { params: payload.requestParams, load: 'noLoad' } });
        yield put(httpRequestsOnSuccessActions.appendSuccess(type));
    } catch (err) {
        yield put(httpRequestsOnErrorsActions.appendError(type, errorOf(err)));
    } finally {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
    }
}

function* editWishlistItem({ type, payload }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(type));
    try {
        const res = yield call(wishlistService.editWishlistItemService, payload.id, payload.body);
        yield put({ type: GET_WISHLIST_ITEM_BY_ID_SUCCESS, payload: res.data });
        yield put({ type: GET_WISHLIST_ITEMS, payload: { params: payload.requestParams, load: 'noLoad' } });
        yield put(httpRequestsOnSuccessActions.appendSuccess(type));
    } catch (err) {
        yield put(httpRequestsOnErrorsActions.appendError(type, errorOf(err)));
    } finally {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
    }
}

function* deleteWishlistItem({ type, payload }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(type));
    try {
        yield call(wishlistService.deleteWishlistItemService, payload.id);
        yield put({ type: GET_WISHLIST_ITEMS, payload: { params: payload.requestParams, load: 'noLoad' } });
        yield put(httpRequestsOnSuccessActions.appendSuccess(type));
    } catch (err) {
        yield put(httpRequestsOnErrorsActions.appendError(type, errorOf(err)));
    } finally {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
    }
}

/** Silent: a dot is not worth a spinner or an error toast. */
function* getWishlistUpdatesCount() {
    try {
        const res = yield call(wishlistService.getWishlistUpdatesCountService);
        yield put({ type: GET_WISHLIST_UPDATES_COUNT_SUCCESS, payload: res.data?.count || 0 });
    } catch (err) {
        // Leave the last known count in place.
    }
}

function* markWishlistSeen({ payload }) {
    try {
        yield call(wishlistService.markWishlistSeenService, payload.id);
    } catch (err) {
        // The re-read below restores the true count either way.
    }
    yield put({ type: GET_WISHLIST_UPDATES_COUNT });
}

export const watchWishlist = function* watchWishlistSaga() {
    yield takeLatest(GET_WISHLIST_UPDATES_COUNT, getWishlistUpdatesCount);
    // Every open must reach the server, not just the last one.
    yield takeEvery(MARK_WISHLIST_SEEN, markWishlistSeen);
    yield takeLatest(GET_WISHLIST_ITEMS, getWishlistItems);
    yield takeLatest(GET_WISHLIST_ITEM_BY_ID, getWishlistItemById);
    yield takeLatest(CREATE_WISHLIST_ITEM, createWishlistItem);
    yield takeLatest(EDIT_WISHLIST_ITEM, editWishlistItem);
    yield takeLatest(DELETE_WISHLIST_ITEM, deleteWishlistItem);
};
